import React, { useEffect, useMemo, useRef, useState } from 'react';
import VectorGround, { CenterVertices } from '../vectorGround/VectorGround';
import {
  buildGroundContext,
  canonicalToCanvas,
  computeViewport,
  localToCanonical,
  normalizeGroundVertices,
  Viewport,
} from '../vectorGround/lib';
import { computeScale, getGroundVertices, getRectangleDimensions } from '../vectorGround/lib/ground';
import { GroundType } from '../../models/ground';
import { computeRectangleCenter, getScale, rotateRectangle } from '../track/ground/calc';
import { coordsGPSToLPS, getSizesFromRef, isSetGlobal } from '../utils/ground';

type Player = {
  id: number
  name: string
  data: {
    timestamp: number
    x: number
    y: number
  }[]
};

const dataGrounds: { [k: string]: GroundType } = {
  default: {
    id: 'default',
    name: 'default',
    groundType: 'BASKET',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 20},
    vertexCX: {value: 40},
    vertexCY: {value: 20},
    vertexDX: {value: 40},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  basket: {
    id: 'basket',
    name: 'basket',
    groundType: 'BASKET',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 15},
    vertexCX: {value: 28},
    vertexCY: {value: 15},
    vertexDX: {value: 28},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  fencing: {
    id: 'fencing',
    name: 'fencing',
    groundType: 'FENCING',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 2},
    vertexCX: {value: 18},
    vertexCY: {value: 2},
    vertexDX: {value: 18},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  football: {
    id: 'football',
    name: 'football',
    groundType: 'FOOTBALL',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 60},
    vertexCX: {value: 100},
    vertexCY: {value: 60},
    vertexDX: {value: 100},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  futsal: {
    id: 'futsal',
    name: 'futsal',
    groundType: 'FUTSAL',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 20},
    vertexCX: {value: 40},
    vertexCY: {value: 20},
    vertexDX: {value: 40},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  volleyball: {
    id: 'volleyball',
    name: 'volleyball',
    groundType: 'VOLLEYBALL',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 9},
    vertexCX: {value: 18},
    vertexCY: {value: 9},
    vertexDX: {value: 18},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  gaelicFootball: {
    id: 'gaelicFootball',
    name: 'gaelicFootball',
    groundType: 'GAELIC_FOOTBALL',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 90},
    vertexCX: {value: 145},
    vertexCY: {value: 90},
    vertexDX: {value: 145},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  handball: {
    id: 'handball',
    name: 'handball',
    groundType: 'HANDBALL',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 20},
    vertexCX: {value: 40},
    vertexCY: {value: 20},
    vertexDX: {value: 40},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  rugby: {
    id: 'rugby',
    name: 'rugby',
    groundType: 'RUGBY',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 60},
    vertexCX: {value: 120},
    vertexCY: {value: 60},
    vertexDX: {value: 120},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  rugbyHalf: {
    id: 'rugby',
    name: 'rugby half',
    groundType: 'RUGBY_HALF',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 50},
    vertexCX: {value: 70},
    vertexCY: {value: 50},
    vertexDX: {value: 70},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  shorttrack: {
    id: 'shorttrack',
    name: 'shorttrack',
    groundType: 'SHORT_TRACK',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 30},
    vertexCX: {value: 60},
    vertexCY: {value: 30},
    vertexDX: {value: 60},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  tennis: {
    id: 'tennis',
    name: 'tennis',
    groundType: 'TENNIS',
    groundCoordsType: 'LOCAL',
    groundSurface: 'GRASS',
    groundSurfaceColor: '#008800',
    vertexAX: {value: 0},
    vertexAY: {value: 0},
    vertexBX: {value: 0},
    vertexBY: {value: 11},
    vertexCX: {value: 24},
    vertexCY: {value: 11},
    vertexDX: {value: 24},
    vertexDY: {value: 0},
    vertexALatitude: 0,
    vertexALongitude: 0,
    vertexBLatitude: 0,
    vertexBLongitude: 0,
    vertexCLatitude: 0,
    vertexCLongitude: 0,
    vertexDLatitude: 0,
    vertexDLongitude: 0,
  },
  'basketGPS': {
    'id': 'basketGPS',
    'name': 'basketGPS',
    'groundType': 'BASKET',
    'groundCoordsType': 'GLOBAL',
    'vertexAX': {'value': 0}, 'vertexAY': {'value': 0},
    'vertexBX': {'value': 0}, 'vertexBY': {'value': 0},
    'vertexCX': {'value': 0}, 'vertexCY': {'value': 0},
    'vertexDX': {'value': 0}, 'vertexDY': {'value': 0},
    'vertexALatitude': 46.0815347, 'vertexALongitude': 13.1973421,
    'vertexBLatitude': 46.0816696, 'vertexBLongitude': 13.1973421,
    'vertexCLatitude': 46.0816696, 'vertexCLongitude': 13.1977042,
    'vertexDLatitude': 46.0815347, 'vertexDLongitude': 13.1977042
  },
  'basketGPS45': {
    'id': 'basketGPS45',
    'name': 'basketGPS45',
    'groundType': 'BASKET',
    'groundCoordsType': 'GLOBAL',
    'vertexALatitude': 46.0815347, 'vertexALongitude': 13.1973421,
    'vertexBLatitude': 46.0816301, 'vertexBLongitude': 13.1972049,
    'vertexCLatitude': 46.0818086, 'vertexCLongitude': 13.1974609,
    'vertexDLatitude': 46.0817132, 'vertexDLongitude': 13.1975981
  },
  'basketCCW': {
    'id': 'basketCCW',
    'name': 'basketCCW',
    'groundType': 'BASKET',
    'groundCoordsType': 'LOCAL',
    'vertexAX': {'value': 0}, 'vertexAY': {'value': 0},
    'vertexBX': {'value': 28}, 'vertexBY': {'value': 0},
    'vertexCX': {'value': 28}, 'vertexCY': {'value': 15},
    'vertexDX': {'value': 0}, 'vertexDY': {'value': 15}
  },
  'basket45': {
    'id': 'basket45',
    'name': 'basket45',
    'groundType': 'BASKET',
    'groundCoordsType': 'LOCAL',
    'vertexAX': {'value': 0}, 'vertexAY': {'value': 0},
    'vertexBX': {'value': -10.61}, 'vertexBY': {'value': 10.61},
    'vertexCX': {'value': 9.19}, 'vertexCY': {'value': 30.41},
    'vertexDX': {'value': 19.80}, 'vertexDY': {'value': 19.80}
  },
  'basket45CCW': {
    'id': 'basket45CCW',
    'name': 'basket45CCW',
    'groundType': 'BASKET',
    'groundCoordsType': 'LOCAL',
    'vertexAX': {'value': 0}, 'vertexAY': {'value': 0},
    'vertexBX': {'value': 19.80}, 'vertexBY': {'value': 19.80},
    'vertexCX': {'value': 9.19}, 'vertexCY': {'value': 30.41},
    'vertexDX': {'value': -10.61}, 'vertexDY': {'value': 10.61}
  },
  'footballGPS': {
    'id': 'footballGPS',
    'name': 'footballGPS',
    'groundType': 'FOOTBALL',
    'groundCoordsType': 'GLOBAL',
    'vertexALatitude': 46.0815347, 'vertexALongitude': 13.1973421,
    'vertexBLatitude': 46.0820743, 'vertexBLongitude': 13.1973421,
    'vertexCLatitude': 46.0820743, 'vertexCLongitude': 13.1986349,
    'vertexDLatitude': 46.0815347, 'vertexDLongitude': 13.1986349
  },
  'football45': {
    'id': 'football45',
    'name': 'football45',
    'groundType': 'FOOTBALL',
    'groundCoordsType': 'LOCAL',
    'vertexAX': {'value': 0}, 'vertexAY': {'value': 0},
    'vertexBX': {'value': -42.43}, 'vertexBY': {'value': 42.43},
    'vertexCX': {'value': 28.28}, 'vertexCY': {'value': 113.14},
    'vertexDX': {'value': 70.71}, 'vertexDY': {'value': 70.71}
  },
  'tennisGPS': {
    'id': 'tennisGPS',
    'name': 'tennisGPS',
    'groundType': 'TENNIS',
    'groundCoordsType': 'GLOBAL',
    'vertexALatitude': 46.0815347, 'vertexALongitude': 13.1973421,
    'vertexBLatitude': 46.0816337, 'vertexBLongitude': 13.1973421,
    'vertexCLatitude': 46.0816337, 'vertexCLongitude': 13.1976524,
    'vertexDLatitude': 46.0815347, 'vertexDLongitude': 13.1976524
  },
  'volleyballCCW': {
    'id': 'volleyballCCW',
    'name': 'volleyballCCW',
    'groundType': 'VOLLEYBALL',
    'groundCoordsType': 'LOCAL',
    'vertexAX': {'value': 0}, 'vertexAY': {'value': 0},
    'vertexBX': {'value': 18}, 'vertexBY': {'value': 0},
    'vertexCX': {'value': 18}, 'vertexCY': {'value': 9},
    'vertexDX': {'value': 0}, 'vertexDY': {'value': 9}
  }
};

export default function TestVectorGround() {
  const boxRef = useRef<HTMLDivElement | null>(null);

  const {
    WINDOW_WIDTH,
  } = getSizesFromRef(boxRef);

  const WINDOW_HEIGHT = Math.floor(WINDOW_WIDTH * 9 / 16);

  const [players, setPlayers] = useState<Player[]>([]);
  const [currentGround, setCurrentGround] = useState<GroundType>(dataGrounds.default);

  const isLPS = currentGround.groundCoordsType === 'LOCAL';

  const groundVertices = getGroundVertices(currentGround);
  const groundVerticesArr = Object.values(groundVertices);

  const groundV = currentGround?.vertexAX || currentGround?.vertexALatitude
    ? normalizeGroundVertices(groundVerticesArr, isLPS ? 'local' : 'gps')
    : undefined;

  const ctx = groundV && buildGroundContext(
    groundV, {
      correctPerspective: groundV?.meta.maxAngleErrorDeg > 0.5,
    }
  ) || undefined;

  const CANVAS_W = WINDOW_WIDTH;
  const CANVAS_H = WINDOW_HEIGHT;
  const PADDING = 0;

  const vp: Viewport = groundV && computeViewport(CANVAS_W, CANVAS_H, ctx, PADDING) || undefined;

  console.log('Ground', vp, CANVAS_W, CANVAS_H, ctx, groundV)

  const {scale: scaleReference} = computeScale(ctx, vp);

  console.log('Scale', scaleReference)

  const corners = {
    A: groundV && canonicalToCanvas(localToCanonical(groundV.A, ctx), vp) || {cx: 0, cy: 0},
    B: groundV && canonicalToCanvas(localToCanonical(groundV.B, ctx), vp) || {cx: 0, cy: 0},
    C: groundV && canonicalToCanvas(localToCanonical(groundV.C, ctx), vp) || {cx: 0, cy: 0},
    D: groundV && canonicalToCanvas(localToCanonical(groundV.D, ctx), vp) || {cx: 0, cy: 0},
  };
  console.log('Corners', corners)

  const centerVertices: CenterVertices = {
    top: {
      x: (corners.B.cx + corners.C.cx) / 2,
      y: (corners.B.cy + corners.C.cy) / 2,
    },
    bottom: {
      x: (corners.A.cx + corners.D.cx) / 2,
      y: (corners.A.cy + corners.D.cy) / 2,
    },
  };

  const [size, setSize] = useState({w: 0, h: 0});

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(( [entry] ) => {
      console.log('DIMMS', Math.round(entry.contentRect.width), Math.round(entry.contentRect.height))
      setSize({
        w: Math.round(entry.contentRect.width),
        h: Math.round(entry.contentRect.height),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const groundGeometry = useMemo(() => {
    if (
      !currentGround
    ) {
      return false;
    }

    if (isLPS) {
      const adjustedData = rotateRectangle({
        A: {
          x: currentGround.vertexAX.value,
          y: currentGround.vertexAY.value,
        },
        B: {
          x: currentGround.vertexBX.value,
          y: currentGround.vertexBY.value,
        },
        C: {
          x: currentGround.vertexCX.value,
          y: currentGround.vertexCY.value,
        },
        D: {
          x: currentGround.vertexDX.value,
          y: currentGround.vertexDY.value,
        },
      });

      // calcolo le dimensioni del campo (dai dati)
      const width = Math.abs(adjustedData.vertices.C.x - adjustedData.vertices.A.x);
      const height = Math.abs(adjustedData.vertices.C.y - adjustedData.vertices.A.y);

      // calcolo il valore della scala per adattare il campo alla canvas
      const scaleValue = getScale(
        width + 10,
        height + 10,
        CANVAS_W,
        CANVAS_H,
      );

      return {
        vertex: {
          vertexA: {
            x: adjustedData.vertices.A.x * scaleValue,
            y: adjustedData.vertices.A.y * scaleValue,
          },
          vertexC: {
            x: adjustedData.vertices.C.x * scaleValue,
            y: adjustedData.vertices.C.y * scaleValue,
          },
          vertexB: {
            x: adjustedData.vertices.B.x * scaleValue,
            y: adjustedData.vertices.B.y * scaleValue,
          },
          vertexD: {
            x: adjustedData.vertices.D.x * scaleValue,
            y: adjustedData.vertices.D.y * scaleValue,
          },
        },
        dims: {width: width * scaleValue, height: height * scaleValue},
        center: {
          x: adjustedData.rotationInfo.centerX * scaleValue,
          y: adjustedData.rotationInfo.centerY * scaleValue,
        },
        angle: adjustedData.rotationInfo.angle * (180 / Math.PI),
        isCW: adjustedData.rotationInfo.isClockwise,
        scale: scaleValue,
      };
    }

    const {vertexA, vertexB, vertexC, vertexD, scale} = coordsGPSToLPS({
      ground: currentGround,
      WINDOW_WIDTH,
      WINDOW_HEIGHT,
    });

    const width = Math.abs(vertexC.x - vertexA.x);
    const height = Math.abs(vertexC.y - vertexA.y);

    const centro = computeRectangleCenter(vertexA, vertexC);

    return {
      vertex: {
        vertexA: {x: vertexA.x * scale, y: -vertexA.y * scale},
        vertexB: {x: vertexB.x * scale, y: -vertexB.y * scale},
        vertexC: {x: vertexC.x * scale, y: -vertexC.y * scale},
        vertexD: {x: vertexD.x * scale, y: -vertexD.y * scale},
      },
      dims: {
        width: width * scale,
        height: height * scale,
      },
      center: {
        x: centro.x * scale,
        y: -centro.y * scale,
      },
      angle: 0,
      isCW: false,
      scale,
    };
  }, [
    currentGround?.vertexAX?.value, currentGround?.vertexAY?.value,
    currentGround?.vertexBX?.value, currentGround?.vertexBY?.value,
    currentGround?.vertexCX?.value, currentGround?.vertexCY?.value,
    currentGround?.vertexDX?.value, currentGround?.vertexDY?.value,
    CANVAS_W, CANVAS_H,
  ]);

  const rectangleDimensions = getRectangleDimensions(
    {x: corners.A.cx, y: corners.A.cy},
    {x: corners.B.cx, y: corners.B.cy},
    {x: corners.C.cx, y: corners.C.cy},
    {x: corners.D.cx, y: corners.D.cy},
  );

  const groundDetails = useMemo(() => {
    if (
      currentGround.groundCoordsType === 'GLOBAL'
      && isSetGlobal(currentGround)
    ) {
      const {
        vertexA,
        vertexB,
        vertexC,
        vertexD,
      } = coordsGPSToLPS({
        ground: currentGround,
        WINDOW_WIDTH,
        WINDOW_HEIGHT,
      });

      return {
        ...currentGround,
        ...{
          vertexAX: {...currentGround.vertexAX, ...{value: vertexA.x}},
          vertexAY: {...currentGround.vertexAY, ...{value: vertexA.y}},
          vertexBX: {...currentGround.vertexBX, ...{value: vertexB.x}},
          vertexBY: {...currentGround.vertexBY, ...{value: vertexB.y}},
          vertexCX: {...currentGround.vertexCX, ...{value: vertexC.x}},
          vertexCY: {...currentGround.vertexCY, ...{value: vertexC.y}},
          vertexDX: {...currentGround.vertexDX, ...{value: vertexD.x}},
          vertexDY: {...currentGround.vertexDY, ...{value: vertexD.y}},
        },
      };
    }

    return currentGround;
  }, [currentGround, currentGround.id, WINDOW_WIDTH, WINDOW_HEIGHT]);

  console.log('vp', vp)

  return (
    <div className="test__vector-ground" style={{width: '100%', height: '700px'}}>
      <select name="ground-select" id="ground-select" onChange={( e ) => setCurrentGround(dataGrounds[e.target.value])}>
        {Object.keys(dataGrounds).map(( key ) => (
          <option key={key} value={key}>{dataGrounds[key].name}</option>
        ))}
      </select>

      <div ref={boxRef}>
        <VectorGround
          centerVertices={centerVertices}
          corners={corners}
          vp={vp}
          groundGeometry={
            groundGeometry
              ? {
                ...groundGeometry,
                ...{
                  dims: rectangleDimensions,
                },
              }
              : undefined
          }
          groundDetails={groundDetails}
          ground={{
            coordType: isLPS ? 'local' : 'gps',
            vertices: [
              {x: currentGround.vertexAX?.value || 0, y: currentGround.vertexAY?.value || 0},
              {x: currentGround.vertexBX?.value || 0, y: currentGround.vertexBY?.value || 0},
              {x: currentGround.vertexCX?.value || 0, y: currentGround.vertexCY?.value || 0},
              {x: currentGround.vertexDX?.value || 0, y: currentGround.vertexDY?.value || 0},
            ],
          }}
          players={players}
          scaleReference={scaleReference}
        />
      </div>
    </div>
  );
}
